import { ref } from 'vue';
import type { Ref } from 'vue';

import type { SelectionIntent } from '@/components/datatable/dataTableTypes';

type GetRowId = (row: any) => string | number | null;

/**
 * Centralized selection state management for the DataTable.
 *
 * Responsibilities:
 * - Keep a stable selection state via `selectedRowIds` (source of truth)
 * - Derive the concrete selected row objects for the current page (`selectedRowsState`)
 * - Support selection behaviors:
 *   - replace (single selection)
 *   - add (multi selection)
 *   - ensure (idempotent add)
 *   - range (shift-selection within current page)
 * - Provide an integration hook (`onSelectionUpdate`) for PrimeVue's `v-model:selection`
 *
 * Design notes:
 * - `selectedRowIds` is the authoritative selection state because row objects
 *   are recreated frequently (pagination, filtering, enrichment, re-rendering).
 * - `selectedRowsState` is re-derived from the current `paginatedData` so that
 *   PrimeVue renders the selection correctly on the visible page.
 * - Range selection is intentionally scoped to `paginatedData` (current page),
 *   since range across full filtered data requires different indexing semantics.
 */
export function useDataTableSelection(options: {
  /** Current page slice shown in the DataTable (used for selection rehydration and range selection) */
  paginatedData: Ref<any[]>;

  /** Row identity function. Must return a stable, unique identifier per row. */
  getRowId: GetRowId;

  /** Optional callback to inform parent components about the current selection (rows, not ids). */
  emitSelectedRows?: (rows: any[]) => void;
}) {
  /**
   * The selected row objects for the current page.
   *
   * This is primarily required for PrimeVue's DataTable selection rendering.
   * It may contain only the subset of selected rows that are currently visible.
   */
  const selectedRowsState = ref<any[]>([]);

  /**
   * Authoritative list of selected row ids (stable across pagination/re-rendering).
   */
  const selectedRowIds = ref<string[]>([]);

  /**
   * Anchor row id used for range selection (Shift + Arrow / Shift + Click semantics).
   *
   * The anchor typically represents the start of the selection range.
   */
  const selectionAnchorRowId = ref<string | number | null>(null);

  /**
   * Returns true if the given row is currently selected (based on its id).
   */
  function isRowSelected(row: any): boolean {
    const id = options.getRowId(row);
    if (id == null) return false;
    return selectedRowIds.value.includes(String(id));
  }

  /**
   * Rehydrates `selectedRowsState` from `selectedRowIds` and the current `paginatedData`.
   *
   * Should be called whenever `paginatedData` changes (pagination, filtering, sorting),
   * so the selection highlight stays consistent for visible rows.
   */
  function reapplySelectionFromIds() {
    if (!selectedRowIds.value.length || !options.paginatedData.value.length) return;

    const rows = options.paginatedData.value.filter((row) => {
      const id = options.getRowId(row);
      return id != null && selectedRowIds.value.includes(String(id));
    });

    if (rows.length) selectedRowsState.value = rows;
  }

  /**
   * Replaces the entire selection with the given row.
   *
   * Also sets the selection anchor to this row id, so a subsequent range selection
   * uses it as the start point.
   */
  function replaceSelectionWithRow(row: any) {
    const id = options.getRowId(row);
    if (id == null) return;
    selectedRowIds.value = [String(id)];
    selectionAnchorRowId.value = id;
    reapplySelectionFromIds();
  }

  /**
   * Adds the given row to the selection (no-op if already selected).
   *
   * If no anchor is set yet, the added row becomes the anchor.
   */
  function addRowToSelection(row: any) {
    const id = options.getRowId(row);
    if (id == null) return;

    const idStr = String(id);
    if (!selectedRowIds.value.includes(idStr)) {
      selectedRowIds.value = [...selectedRowIds.value, idStr];
    }

    if (!selectionAnchorRowId.value) selectionAnchorRowId.value = id;

    reapplySelectionFromIds();
  }

  /**
   * Ensures the given row is selected (idempotent add).
   *
   * Useful for scenarios where a row must be selected before an action happens
   * (e.g. entering edit mode via keyboard navigation).
   */
  function ensureRowSelected(row: any) {
    const id = options.getRowId(row);
    if (id == null) return;

    const idStr = String(id);
    if (selectedRowIds.value.includes(idStr)) return;

    selectedRowIds.value = [...selectedRowIds.value, idStr];
    reapplySelectionFromIds();
  }

  /**
   * Selects a contiguous range on the current page from the anchor row to `targetRow`.
   *
   * Notes:
   * - Range selection is bounded to `paginatedData` (current page).
   * - If no anchor exists yet, the target becomes the anchor, resulting in a single-row selection.
   */
  function selectRangeToRow(targetRow: any) {
    const targetId = options.getRowId(targetRow);
    if (targetId == null) return;

    const anchorId = selectionAnchorRowId.value ?? targetId;

    const rows = options.paginatedData.value;
    const startIndex = rows.findIndex((r) => String(options.getRowId(r)) === String(anchorId));
    const endIndex = rows.findIndex((r) => String(options.getRowId(r)) === String(targetId));
    if (startIndex === -1 || endIndex === -1) return;

    const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
    selectedRowIds.value = rows.slice(from, to + 1).map((r) => String(options.getRowId(r)));

    reapplySelectionFromIds();
  }

  /**
   * Applies a selection action for a single row, based on a normalized intent.
   *
   * Typical mapping:
   * - replace: plain click / Arrow navigation
   * - add: Alt/Ctrl + click (multi add)
   * - range: Shift + click (range)
   * - ensure: helper for "select before doing something"
   * - none: do nothing
   */
  function applySelectionIntent(row: any, intent: SelectionIntent) {
    switch (intent) {
      case 'none':
        return;
      case 'ensure':
        ensureRowSelected(row);
        return;
      case 'add':
        addRowToSelection(row);
        return;
      case 'range':
        selectRangeToRow(row);
        return;
      case 'replace':
      default:
        replaceSelectionWithRow(row);
        return;
    }
  }

  /**
   * Synchronizes internal selection state when PrimeVue emits selection updates.
   *
   * This is designed to be used with:
   * - `@update:selection="onSelectionUpdate"`
   * - or `v-model:selection="selectedRowsState"` plus explicit handler
   *
   * It updates:
   * - `selectedRowsState` (selected objects for the current page)
   * - `selectedRowIds` (authoritative selection identity state)
   * - optional `emitSelectedRows` callback to inform the parent
   */
  function onSelectionUpdate(v: any) {
    const next = Array.isArray(v) ? v : v ? [v] : [];
    selectedRowsState.value = next;

    selectedRowIds.value = next
      .map((row) => {
        const id = options.getRowId(row);
        return id != null ? String(id) : null;
      })
      .filter((id): id is string => id !== null);

    options.emitSelectedRows?.(next);
  }

  return {
    selectedRowsState,
    selectedRowIds,
    selectionAnchorRowId,
    isRowSelected,
    reapplySelectionFromIds,
    replaceSelectionWithRow,
    addRowToSelection,
    ensureRowSelected,
    selectRangeToRow,
    applySelectionIntent,
    onSelectionUpdate,
  };
}
